import React from "react";
import { styled, lighten, darken, useTheme } from "@mui/material";
import { 
  DataGrid, 
  GridRowSelectionModel,
  GridFilterModel,
  GridSortModel,
  GridPaginationModel,
  GridColumnResizeParams
} from "@mui/x-data-grid";
import { colorMapper } from "../colorUtils.ts";
import Toolbar from "./Toolbar.tsx";
import visibilityStates from "./visibilityStates.ts";

interface StyledDataGridProps {
  rows: any[];
  columns: any[];
  rowSelectionModel: GridRowSelectionModel;
  processRowUpdate?: (newRow: any) => any;
  setRowSelectionModel: (newModel: GridRowSelectionModel) => void;
  maxHeight?: string;
  handleBulkAction: (action: string) => void;
  handleVisibilityChange: (stateName: keyof typeof visibilityStates) => void;
  columnVisibility: any;
  setColumnVisibility: any;
  openReportOverlay: () => void;
  openBulkActionOverlay: () => void;
  
  // New props for persistent state
  filterModel?: GridFilterModel;
  onFilterModelChange?: (newModel: GridFilterModel) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (newModel: GridSortModel) => void;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (newModel: GridPaginationModel) => void;
  onColumnResize?: (params: GridColumnResizeParams) => void;
  pageSizeOptions?: number[];
  disableColumnFilter?: boolean;
  disableColumnSelector?: boolean;
  disableDensitySelector?: boolean;
}

const StyledDataGridComponent = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-root": {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  "& .MuiDataGrid-row": {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  "& .MuiDataGrid-cell": {
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  ...Array.from({ length: 11 }).reduce((acc: Record<string, any>, _, idx) => {
    const rowColor = theme.palette.mode === "dark"
      ? darken(colorMapper(idx), 0.3) // Darken colors in dark mode
      : lighten(colorMapper(idx), 0.2);

    const textColor = "#000";

    acc[`& .row-color-${idx}`] = {
      backgroundColor: rowColor,
      color: textColor, // Dynamic text color
      "&:hover": {
        backgroundColor: darken(rowColor, 0.2),
      },
    };
    return acc;
  }, {}),
}));

const StyledDataGrid: React.FC<StyledDataGridProps> = ({
  rows,
  columns,
  rowSelectionModel,
  processRowUpdate,
  setRowSelectionModel,
  maxHeight = "100%",
  handleBulkAction,
  handleVisibilityChange,
  columnVisibility,
  setColumnVisibility,
  openReportOverlay,
  openBulkActionOverlay,
  // New props
  filterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  paginationModel,
  onPaginationModelChange,
  onColumnResize,
  pageSizeOptions = [25, 50, 100],
  disableColumnFilter = false,
  disableColumnSelector = false,
  disableDensitySelector = false,
}) => {
  const theme = useTheme();

  const getRowClassName = (params: any) => `row-color-${params.row.color_choice}`;

  return (
    <StyledDataGridComponent
      checkboxSelection
      disableRowSelectionOnClick
      rows={rows}
      columns={columns}
      onRowSelectionModelChange={(newSelection) => setRowSelectionModel(newSelection)}
      processRowUpdate={processRowUpdate}
      rowSelectionModel={rowSelectionModel}
      getRowClassName={getRowClassName}
      columnVisibilityModel={columnVisibility}
      onColumnVisibilityModelChange={(newModel) => {
        setColumnVisibility(newModel);
      }}
      
      // Add the new controlled state props
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onColumnResize={onColumnResize}
      
      // Pagination options
      pageSizeOptions={pageSizeOptions}
      
      // Feature flags
      disableColumnFilter={disableColumnFilter}
      disableColumnSelector={disableColumnSelector}
      disableDensitySelector={disableDensitySelector}
      
      slots={{
        toolbar: () => (
          <Toolbar
            handleBulkAction={handleBulkAction}
            handleVisibilityChange={handleVisibilityChange}
            openReportOverlay={openReportOverlay}
            openBulkActionOverlay={openBulkActionOverlay}
          />
        ),
      }}
      sx={{
        boxShadow: theme.shadows[2],
        border: `2px solid ${theme.palette.divider}`,
        maxHeight: maxHeight,
        flexGrow: maxHeight === "100%" ? 1 : 0,
        "& .MuiDataGrid-main": {
          maxHeight: `calc(${maxHeight} - 120px)`, // Account for header and pagination
        },
      }}
    />
  );
};

export default StyledDataGrid;